import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { analytics } from '@/lib/analytics';
import { normalizeEmail } from '@/lib/email';
import { cn } from '@/lib/utils';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AuthLogo } from './AuthLogo';
import { InputWithError } from './InputWithError';

export const SignupForm = ({ className, ...props }: React.ComponentProps<'div'>) => {
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { t } = useTranslation('auth');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const [referrerName, setReferrerName] = useState<string | null>(null);
  const [referralExpired, setReferralExpired] = useState(false);
  const referralToken = searchParams.get('ref');

  useEffect(() => {
    if (!referralToken) return;
    fetch(`${import.meta.env.VITE_ENDPOINT}/referral-info?token=${encodeURIComponent(referralToken)}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.found) setReferrerName(data.referrerName);
        else if (data.expired) setReferralExpired(true);
      })
      .catch(() => {});
  }, [referralToken]);

  const formik = useFormik({
    initialValues: {
      email: '',
      firstName: '',
      lastName: '',
      password: '',
      confirmPassword: '',
    },
    validate: (values) => {
      const errors: Record<string, string> = {};

      if (!values.email.trim()) {
        errors.email = t('signup.emailRequired');
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
        errors.email = t('signup.invalidEmail');
      }

      if (!values.firstName.trim()) {
        errors.firstName = t('signup.firstNameRequired');
      }

      if (!values.lastName.trim()) {
        errors.lastName = t('signup.lastNameRequired');
      }

      if (!values.password) {
        errors.password = t('signup.passwordRequired');
      } else if (values.password.length < 8) {
        errors.password = t('signup.passwordMinLength');
      }

      if (!values.confirmPassword) {
        errors.confirmPassword = t('signup.confirmPasswordRequired');
      } else if (values.password !== values.confirmPassword) {
        errors.confirmPassword = t('signup.passwordsNoMatch');
      }

      return errors;
    },
    onSubmit: async (values) => {
      setIsLoading(true);
      setError(null);

      const toastId = toast.loading(t('signup.creatingAccount'), {
        description: t('signup.creatingAccountDescription'),
      });

      try {
        const captchaToken = await executeRecaptcha?.('signup');

        const response = await fetch(`${import.meta.env.VITE_ENDPOINT}/signup`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: normalizeEmail(values.email),
            password: values.password,
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            captchaToken,
            ...(referralToken && { referralToken }),
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          if (data.error === 'DISPOSABLE_EMAIL_DOMAIN') throw new Error(t('signup.disposableEmail'));
          throw new Error(data.error || t('signup.failedCreateAccount'));
        }

        if (data.success) {
          analytics.capture('Signup Completed', { hasReferral: !!referralToken });
          toast.success(t('signup.verificationCodeSent'), {
            description: t('signup.verificationCodeSentDescription'),
            id: toastId,
          });
          const params = new URLSearchParams({ secret: data.secret, email: normalizeEmail(values.email) });
          navigate(`/auth/verify?${params.toString()}`);
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : t('signup.failedCreateAccount');
        setError(errorMessage);
        toast.error(t('signup.failedCreateAccount'), {
          description: errorMessage,
          id: toastId,
        });
      } finally {
        setIsLoading(false);
      }
    },
  });

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      <Card className='overflow-hidden p-0'>
        <CardContent className='grid p-0 md:grid-cols-2 bg-white dark:bg-stone-900'>
          <form className='p-6 md:p-8' onSubmit={formik.handleSubmit}>
            <div className='flex flex-col gap-6'>
              <div className='flex flex-col items-center text-center'>
                <AuthLogo />
                <h1 className='text-2xl font-bold'>{t('signup.createAccount')}</h1>
                <p className='text-muted-foreground text-balance'>{t('signup.getStarted')}</p>
                {referrerName && (
                  <div className='mt-4 p-3 bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-800 rounded-md'>
                    <p className='text-sm text-blue-600 dark:text-blue-400'>{t('referral.invitedBy', { name: referrerName })}</p>
                  </div>
                )}
                {referralExpired && (
                  <div className='mt-4 p-3 bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-800 rounded-md'>
                    <p className='text-sm text-amber-600 dark:text-amber-400'>{t('referral.linkExpired')}</p>
                  </div>
                )}
                {error && (
                  <div className='mt-4 p-3 bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-800 rounded-md'>
                    <p className='text-sm text-red-600 dark:text-red-400'>{error}</p>
                  </div>
                )}
              </div>
              <InputWithError
                label={t('signup.email')}
                id='email'
                name='email'
                type='email'
                placeholder={t('signup.emailPlaceholder')}
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email ? formik.errors.email : undefined}
                touched={formik.touched.email}
                required
                disabled={isLoading}
              />
              <div className='grid grid-cols-2 gap-4'>
                <InputWithError
                  label={t('signup.firstName')}
                  id='firstName'
                  name='firstName'
                  type='text'
                  placeholder={t('signup.firstNamePlaceholder')}
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.firstName ? formik.errors.firstName : undefined}
                  touched={formik.touched.firstName}
                  required
                  disabled={isLoading}
                />
                <InputWithError
                  label={t('signup.lastName')}
                  id='lastName'
                  name='lastName'
                  type='text'
                  placeholder={t('signup.lastNamePlaceholder')}
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.lastName ? formik.errors.lastName : undefined}
                  touched={formik.touched.lastName}
                  required
                  disabled={isLoading}
                />
              </div>
              <InputWithError
                label={t('signup.password')}
                id='password'
                name='password'
                type='password'
                placeholder={t('signup.passwordPlaceholder')}
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.password ? formik.errors.password : undefined}
                touched={formik.touched.password}
                required
                disabled={isLoading}
              />
              <InputWithError
                label={t('signup.confirmPassword')}
                id='confirmPassword'
                name='confirmPassword'
                type='password'
                placeholder={t('signup.confirmPasswordPlaceholder')}
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.confirmPassword ? formik.errors.confirmPassword : undefined}
                touched={formik.touched.confirmPassword}
                required
                disabled={isLoading}
              />
              <Button type='submit' className='w-full' disabled={isLoading}>
                {isLoading ? t('signup.creatingAccount') : t('signup.createAccountButton')}
              </Button>
              <div className='text-center text-sm'>
                {t('signup.alreadyHaveAccount')}{' '}
                <Link to='/auth/login' className='underline underline-offset-4 hover:text-primary'>
                  {t('signup.signIn')}
                </Link>
              </div>
            </div>
          </form>
          <div className='bg-muted relative hidden md:block p-4'>
            <div className='absolute inset-0 p-4'>
              <img
                src='/login.png'
                alt='Image'
                className='w-full h-full object-contain '
              />
            </div>
          </div>
        </CardContent>
      </Card>
      <div className='text-muted-foreground *:[a]:hover:text-primary text-center text-xs text-balance *:[a]:underline *:[a]:underline-offset-4'>
        By clicking continue, you agree to our <a href='https://skygis.cloud/terms'>{t('login.termsOfService')}</a> and{' '}
        <a href='https://skygis.cloud/terms'>{t('login.privacyPolicy')}</a>.
      </div>
    </div>
  );
};
